import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { handleUnauthorized } from "../../utils/apiHelper";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DangerAlert from "../child/Alert/DengerAlert";
import Loader from "../child/Loder";
import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";
import { Icon } from "@iconify/react/dist/iconify.js";

const AddInterestPageNew = () => {
  const defaultImageUrl = `${process.env.REACT_APP_PUBLIC_UR}/assets/images/user-grid/169x105.png`;
  const [imagePreview, setImagePreview] = useState(defaultImageUrl);
  const [loading, setLoading] = useState(false);
  const [interest_name, setInterest_name] = useState("");
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [resizedImageBlob, setResizedImageBlob] = useState(null);
  const [imagechange, setImagechange] = useState(0);

  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const authToken = localStorage.getItem("authToken");

  const s3Client = new S3Client({
    region: process.env.REACT_APP_AWS_REGION,
    credentials: {
      accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
      secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
    },
  });


  useEffect(() => {
    const fetchInterest = async () => {
      if (!isEditMode) return;

      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/admin/categoryById/${id}`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );

        if (response.status === 401) {
          handleUnauthorized(navigate);
          return;
        }

        const data = await response.json();
        if (response.ok) {

          setInterest_name(data.result.title || "");
          setImagePreview(data.result.image || defaultImageUrl);
        } else {
          setErrorMessage("Error fetching Interest");
          clearMessage();
        }
      } catch {
        setErrorMessage("Error fetching Interest");
        clearMessage();
      } finally {
        setLoading(false);
      }
    };

    fetchInterest();
  }, [id, navigate, authToken, isEditMode]);

  const uploadToS3 = async (file) => {
    const fileKey = `temp/${Date.now()}_${file.name}`;
    const params = {
      Bucket: process.env.REACT_APP_AWS_S3_BUCKET_NAME,
      Key: fileKey,
      Body: await file.arrayBuffer(),
      ContentType: file.type,
      ACL: "public-read",
    };

    const command = new PutObjectCommand(params);
    await s3Client.send(command);

    return `https://${process.env.REACT_APP_AWS_S3_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${fileKey}`;
  };

  const resetImageState = (message) => {
    setImagePreview(defaultImageUrl);
    setResizedImageBlob(null);
    setImagechange(0);
    setErrors((prev) => ({ ...prev, imagePreview: message }));
  };

  const readURL = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
      resetImageState("Only JPG or PNG files are allowed.");
      return;
    }

    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => {
        if (img.width < 167 || img.height < 103) {
          resetImageState("Image must be at least 167×103 pixels.");
          return;
        }

        const canvas = document.createElement("canvas");
        canvas.width = 167;
        canvas.height = 103;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, 167, 103);

        const previewUrl = canvas.toDataURL(
          file.type.includes("jpeg") ? "image/jpeg" : "image/png"
        );
        setImagePreview(previewUrl);
        setImagechange(1);
        setErrors((prev) => ({ ...prev, imagePreview: "" }));

        canvas.toBlob(
          (blob) => {
            if (blob) {
              setResizedImageBlob(
                new File([blob], file.name, { type: file.type })
              );
            } else {
              resetImageState("Failed to process image.");
            }
          },
          file.type.includes("jpeg") ? "image/jpeg" : "image/png"
        );
      };

      img.onerror = () => {
        resetImageState("Invalid image file.");
      };

      img.src = e.target.result;
    };

    reader.readAsDataURL(file);
  };

  const clearMessage = () => {
    setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 3000);
  };


  const validateInputs = () => {
    const newErrors = {};
    if (!interest_name.trim()) {
      newErrors.interest_name = "Title is required";
    } else if (interest_name.trim().length < 3) {
      newErrors.interest_name = "Title must be at least 3 characters";
    }
    // else if (!/^[A-Za-z\s]+$/.test(interest_name.trim())) {
    //   newErrors.interest_name = "Name can contain only letters and spaces";
    // }

    if (imagePreview === defaultImageUrl && imagechange === 0) {
      newErrors.imagePreview = "Image is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateInputs()) return;

    setLoading(true);

    try {
      let uploadedProfilePictureUrl = imagePreview;

      if (imagechange === 1 && resizedImageBlob) {
        uploadedProfilePictureUrl = await uploadToS3(resizedImageBlob);
      }

      const trimmedName = interest_name.trim();
      const method = isEditMode ? "PUT" : "POST";
      const API_ENDPOINT = `${process.env.REACT_APP_BASE_URL}/admin/${
        isEditMode ? `categoryUpdate/${id}` : "categoryAdd"
      }`;

      const response = await fetch(API_ENDPOINT, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          title: trimmedName,
          image: uploadedProfilePictureUrl,
          status: "active",
        }),
      });

      const data = await response.json();
      if (response.ok) {
        if (data.message === "This data already exists.") {
          setErrorMessage(data.message);
        } else {
          setSuccessMessage(
            data.message || (isEditMode ? "Category Updated" : "Category Added")
          );
          setTimeout(() => navigate("/category"), 3000);
        }
      } else {
        setErrorMessage(data.message || "Something went wrong");
      }
    } catch (err) {
      setErrorMessage(err.message || "Something went wrong");
    } finally {
      setLoading(false);
      clearMessage();
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="row gy-4">
      <div className="col-lg-12">
        <div className="card">
          <div className="card-body">
            {successMessage && <SuccessAlert message={successMessage} />}
            {errorMessage && <DangerAlert message={errorMessage} />}

            <form className="row gy-3 needs-validation" noValidate onSubmit={handleSubmit}>
              <div className="mb-24 mt-16">
                <label className="form-label">
                  Image <span className="text-danger-600">*</span>
                </label>
                <div className="avatar-upload avatar-upload-new" style={{ width: 169, height: 105 }}>
                  <div className="avatar-edit position-absolute bottom-0 end-0 z-1 cursor-pointer">
                    <input
                      type="file"
                      id="imageUpload"
                      accept=".png,.jpg,.jpeg"
                      hidden
                      onChange={readURL}
                    />
                    <label
                      htmlFor="imageUpload"
                      className="d-flex justify-content-center align-items-center bg-primary-50 text-primary-600 border border-primary-600 bg-hover-primary-100 rounded-circle p-1"
                      style={{position:"absolute", right:"-5px", bottom:"-5px"}}
                    >
                      <Icon icon="solar:camera-outline" className="icon" style={{width:"18px", height:"18px"}} />
                    </label>
                  </div>
                  <div className="avatar-preview" style={{ width: 169, height: 105, borderRadius:"15px" }}>
                    <div
                      id="imagePreview"
                      style={{
                        backgroundImage: `url("${imagePreview}")`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        width: 167,
                        height: 103,
                        borderRadius:"15px",
                      }}
                    />
                  </div>
                </div>
                {errors.imagePreview && (
                  <p className="text-danger mt-1">{errors.imagePreview}</p>
                )}
              </div>

              <div className="col-md-12">
                <label className="form-label">
                  Category Name <span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.interest_name ? "is-invalid" : ""}`}
                  id="interest_name"
                  placeholder="Enter Category Name"
                  value={interest_name}
                  minLength="3"
                  maxLength="40"
                  onChange={(e) => {
                    setInterest_name(e.target.value);
                    setErrors((prev) => ({ ...prev, interest_name: "" }));
                  }}
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/\s{2,}/g, " ").trimStart();
                  }}
                />
                {errors.interest_name && (
                  <p className="text-danger mt-1">{errors.interest_name}</p>
                )}
              </div>

              <div className="col-12">
                <button
                  className="btn btn-primary mt-3 float-end"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddInterestPageNew;
